import axios from 'axios';
import {
  Contact,
  ContactTag,
  CreateContactData,
  UpdateContactData,
  ContactFilters,
  ContactResponse,
  CreateContactTagData,
  UpdateContactTagData,
  ShareContactData
} from '../types/contact';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/contacts`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

class ContactService {
  async getContacts(filters: ContactFilters = {}): Promise<ContactResponse> {
    const response = await apiClient.get<ContactResponse>('', { params: filters });
    return response.data;
  }

  async getContact(id: number): Promise<Contact> {
    const response = await apiClient.get<Contact>(`/${id}`);
    return response.data;
  }

  async searchContacts(query: string): Promise<Contact[]> {
    const response = await apiClient.get<Contact[]>('/search', {
      params: { q: query }
    });
    return response.data;
  }

  async createContact(contactData: CreateContactData): Promise<Contact> {
    const response = await apiClient.post<Contact>('', contactData);
    return response.data;
  }

  async updateContact(id: number, contactData: UpdateContactData): Promise<Contact> {
    const response = await apiClient.put<Contact>(`/${id}`, contactData);
    return response.data;
  }

  async deleteContact(id: number): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(`/${id}`);
    return response.data;
  }

  async bulkDeleteContacts(contactIds: number[]): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>('/bulk-delete', {
      contactIds
    });
    return response.data;
  }

  async downloadVCard(id: number): Promise<Blob> {
    const response = await apiClient.get(`/${id}/vcard`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async shareContact(id: number, shareData: ShareContactData): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(`/${id}/share`, shareData);
    return response.data;
  }

  // Contact Tags
  async getTags(): Promise<ContactTag[]> {
    const response = await apiClient.get<ContactTag[]>('/tags');
    return response.data;
  }

  async getTag(id: number): Promise<ContactTag> {
    const response = await apiClient.get<ContactTag>(`/tags/${id}`);
    return response.data;
  }

  async createTag(tagData: CreateContactTagData): Promise<ContactTag> {
    const response = await apiClient.post<ContactTag>('/tags', tagData);
    return response.data;
  }

  async updateTag(id: number, tagData: UpdateContactTagData): Promise<ContactTag> {
    const response = await apiClient.put<ContactTag>(`/tags/${id}`, tagData);
    return response.data;
  }

  async deleteTag(id: number): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(`/tags/${id}`);
    return response.data;
  }
}

export const contactService = new ContactService();

